import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Card, StatCard, PageTitle, StatusBadge, fmtMoney, fmtDateTime, Th, Td, STATUS_LABELS } from "@/admin/ui";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/analytics?days=14").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  if (!stats) return <p className="font-mono text-xs text-graphite">Loading…</p>;

  return (
    <div data-testid="admin-dashboard-page">
      <PageTitle title="Dashboard" sub="The pulse of SYNC — at a glance." />
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Total Revenue" value={fmtMoney(stats.revenue_total)} sub={`${fmtMoney(stats.revenue_month)} this month`} accent="text-signal" testid="stat-revenue" />
        <StatCard label="Orders" value={stats.total_orders} sub={`${stats.orders_by_status.new} new`} testid="stat-orders" />
        <StatCard label="Visitors Today" value={stats.visitors_today} sub="unique visitors" testid="stat-visitors" />
        <StatCard label="Customers" value={stats.customers} sub={`${stats.launch_list} on launch list`} testid="stat-customers" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
        {Object.entries(stats.orders_by_status).map(([s, n]) => (
          <Card key={s} className="p-4 flex items-center justify-between" data-testid={`status-count-${s}`}>
            <StatusBadge status={s} />
            <span className="font-anton text-2xl">{n}</span>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-5 lg:col-span-2" data-testid="dashboard-chart">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">Visitors — last 14 days</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics?.daily || []}>
                <defs>
                  <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00B8A9" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00B8A9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2A2B2E" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#5B5E66", fontSize: 10, fontFamily: "Space Mono" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#5B5E66", fontSize: 10, fontFamily: "Space Mono" }} allowDecimals={false} width={32} />
                <Tooltip contentStyle={{ background: "#17181B", border: "1px solid #2A2B2E", color: "#EDEEF0", fontFamily: "Space Mono", fontSize: 12 }} />
                <Area type="monotone" dataKey="visitors" stroke="#00B8A9" fill="url(#gV)" strokeWidth={2} name="Visitors" />
                <Area type="monotone" dataKey="pageviews" stroke="#9CA3AF" fill="transparent" strokeWidth={1.5} name="Pageviews" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5" data-testid="dashboard-latest-orders">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite">Latest orders</p>
            <Link to="/admin/orders" className="font-mono text-[10px] uppercase tracking-wider text-signal hover:text-chalk">View all →</Link>
          </div>
          <div className="space-y-3">
            {stats.latest_orders.length === 0 && <p className="text-graphite text-sm">No orders yet.</p>}
            {stats.latest_orders.map((o) => (
              <div key={o.session_id} className="flex items-center justify-between border-b border-border/50 pb-3">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-chalk">{o.order_number || o.session_id.slice(-8)}</p>
                  <p className="font-mono text-[10px] text-graphite truncate">{o.customer_email || "—"} · {fmtDateTime(o.created_at)}</p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="font-mono text-xs">{fmtMoney(o.amount_total ?? o.amount)}</p>
                  <StatusBadge status={o.fulfillment_status || "new"} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
